import { useQuery } from '@tanstack/react-query';
import { btnSecondary } from '@/components/admin/FormComponents';
import { cccdAdmin } from '@/lib/api/cccdAdminClient';

const PUNTOS_BASE_URL = 'https://puntos.cccd.space';

export function liveResultsUrl(id: string): string {
  return `${PUNTOS_BASE_URL}/public/live/${id}`;
}

export function launchLiveUrl(id: string): string {
  return `${PUNTOS_BASE_URL}/public/launch/${id}`;
}

// Botones para abrir, en una pestaña nueva, las páginas públicas de
// proyección en pantalla durante el evento (puntos.cccd.space, repo
// aparte) — mismo componente para eventos nacionales y regionales, solo
// cambia el id. El backend público ya soporta ambos (GET /launch-control/
// public/events/:id y .../public/regionals/:id — cccd-scoring-global-
// parameters); la página pública en `puntos` para regionales queda
// pendiente de una sesión con acceso a ese repo, el botón queda listo para
// cuando exista.
// Un enlace deshabilitado de verdad: no navega y dice por qué (title + aria-disabled).
function DisabledProjection({ label, reason }: { label: string; reason: string }) {
  return (
    <span
      role="link"
      aria-disabled="true"
      title={reason}
      className={`min-h-11 cursor-not-allowed opacity-50 ${btnSecondary}`}
    >
      {label}
    </span>
  );
}

export function ProjectionButtons({ id, kind = 'event' }: { id: string; kind?: 'event' | 'regional' }) {
  // Misma clave que EventDetailPage ('event-categories'): React Query reutiliza la respuesta, sin otra petición.
  const { data: categories } = useQuery({
    queryKey: kind === 'event' ? ['event-categories', id] : ['regional-categories', id],
    queryFn: () => (kind === 'event' ? cccdAdmin.getEventCategories(id) : cccdAdmin.getRegionalCategories(id)),
    staleTime: 30_000,
  });
  // Solo se bloquea con datos en mano (hay categorías y ninguna en vivo): si la consulta falla o viene vacía, el botón queda activo.
  const noLiveCategory = Array.isArray(categories) && categories.length > 0 && !categories.some((c) => c.isLive);

  return (
    <>
      {kind === 'regional' ? (
        <DisabledProjection
          label="Proyectar Resultados Generales"
          reason="La página pública de resultados para eventos regionales todavía no está disponible."
        />
      ) : (
        <a href={liveResultsUrl(id)} target="_blank" rel="noopener noreferrer" className={`min-h-11 ${btnSecondary}`}>
          Proyectar Resultados Generales
        </a>
      )}
      {noLiveCategory ? (
        <DisabledProjection
          label="Proyectar Lanzamientos"
          reason='Marca una categoría como "en vivo" en la pestaña Lanzamiento para proyectar los lanzamientos.'
        />
      ) : (
        <a href={launchLiveUrl(id)} target="_blank" rel="noopener noreferrer" className={`min-h-11 ${btnSecondary}`}>
          Proyectar Lanzamientos
        </a>
      )}
    </>
  );
}
